/**
 * Centralized Infrastructure Configuration
 * 
 * AWS resources, deployment settings, and infrastructure-related configuration
 * used across API, infrastructure package, and deployment scripts.
 */

import type { Environment } from './environments';

export interface AWSConfig {
  region: string;
  accountId: string;
  
  // S3 Configuration
  s3: {
    bucketName: string;
    cloudfrontDomainName?: string;
    maxFileSize: number; // bytes
  };
  
  // DynamoDB Configuration
  dynamodb: {
    tableName: string;
    region: string;
  };
  
  // Lambda Configuration
  lambda: {
    functionName: string;
    runtime: string;
    timeout: number; // seconds
    memorySize: number; // MB
    environment: Record<string, string>;
  };
  
  // API Gateway Configuration
  apiGateway: {
    domainName: string;
    certificateArn?: string;
    throttling: {
      rateLimit: number;
      burstLimit: number;
    };
  };
  
  // Secrets Manager
  secretsManager: {
    secretName: string;
    region: string;
  };
  
  // CloudFront Configuration
  cloudfront: {
    webDistributionId?: string;
    apiDistributionId?: string;
    cachePolicyId?: string;
  };
}

export interface DomainConfig {
  primary: string;
  api: string;
  www?: string;
  cdn?: string;
}

export interface DeploymentConfig {
  stackName: string;
  environment: Environment;
  aws: AWSConfig;
  domains: DomainConfig;
  
  // Deployment settings
  deployment: {
    enableCloudFront: boolean;
    enableCustomDomain: boolean;
    enableSsl: boolean;
    enableWaf: boolean;
    enableCloudWatch: boolean;
  };
  
  // Monitoring
  monitoring: {
    enableErrorAlerts: boolean;
    enablePerformanceAlerts: boolean;
    logRetentionDays: number;
    enableXRayTracing: boolean;
  };
}

// AWS Account ID (same across environments)
const AWS_ACCOUNT_ID = '563687506272';
const AWS_REGION = 'us-east-1';

// Infrastructure configurations by environment
const INFRASTRUCTURE_CONFIGS: Record<Environment, DeploymentConfig> = {
  local: {
    stackName: 'safermark-local',
    environment: 'local',
    
    domains: {
      primary: 'localhost:5173',
      api: 'localhost:3001',
    },
    
    aws: {
      region: AWS_REGION,
      accountId: AWS_ACCOUNT_ID,
      
      s3: {
        bucketName: 'safermark-vaults-dev-563687506272', // Use dev resources for local
        maxFileSize: 10 * 1024 * 1024, // 10MB
      },
      
      dynamodb: {
        tableName: 'safermark-users-dev', // Use dev table for local
        region: AWS_REGION,
      },
      
      lambda: {
        functionName: 'safermark-api-dev',
        runtime: 'nodejs20.x',
        timeout: 30,
        memorySize: 512,
        environment: {
          NODE_ENV: 'development',
          SAFERMARK_ENV: 'local',
        },
      },
      
      apiGateway: {
        domainName: 'api.safermark.dev.lbsoft.co.nz',
        throttling: {
          rateLimit: 1000,
          burstLimit: 2000,
        },
      },
      
      secretsManager: {
        secretName: 'safermark/dev/secrets',
        region: AWS_REGION,
      },
      
      cloudfront: {},
    },
    
    deployment: {
      enableCloudFront: false,
      enableCustomDomain: false,
      enableSsl: false,
      enableWaf: false,
      enableCloudWatch: true,
    },
    
    monitoring: {
      enableErrorAlerts: false,
      enablePerformanceAlerts: false,
      logRetentionDays: 7,
      enableXRayTracing: false,
    },
  },
  
  dev: {
    stackName: 'safermark-dev',
    environment: 'dev',
    
    domains: {
      primary: 'safermark.dev.lbsoft.co.nz',
      api: 'api.safermark.dev.lbsoft.co.nz',
      www: 'www.safermark.dev.lbsoft.co.nz',
    },
    
    aws: {
      region: AWS_REGION,
      accountId: AWS_ACCOUNT_ID,
      
      s3: {
        bucketName: 'safermark-vaults-dev-563687506272',
        maxFileSize: 10 * 1024 * 1024, // 10MB
      },
      
      dynamodb: {
        tableName: 'safermark-users-dev',
        region: AWS_REGION,
      },
      
      lambda: {
        functionName: 'safermark-api-dev',
        runtime: 'nodejs20.x',
        timeout: 30,
        memorySize: 512,
        environment: {
          NODE_ENV: 'development',
          SAFERMARK_ENV: 'dev',
        },
      },
      
      apiGateway: {
        domainName: 'api.safermark.dev.lbsoft.co.nz',
        throttling: {
          rateLimit: 1000,
          burstLimit: 2000,
        },
      },
      
      secretsManager: {
        secretName: 'safermark/dev/secrets',
        region: AWS_REGION,
      },
      
      cloudfront: {
        // Will be populated after initial deployment
      },
    },
    
    deployment: {
      enableCloudFront: true,
      enableCustomDomain: true,
      enableSsl: true,
      enableWaf: true,
      enableCloudWatch: true,
    },
    
    monitoring: {
      enableErrorAlerts: true,
      enablePerformanceAlerts: false,
      logRetentionDays: 14,
      enableXRayTracing: true,
    },
  },
  
  prod: {
    stackName: 'safermark-prod',
    environment: 'prod',
    
    domains: {
      primary: 'safermark.com',
      api: 'api.safermark.com',
      www: 'www.safermark.com',
      cdn: 'cdn.safermark.com',
    },
    
    aws: {
      region: AWS_REGION,
      accountId: AWS_ACCOUNT_ID,
      
      s3: {
        bucketName: 'safermark-vaults-prod-563687506272',
        maxFileSize: 10 * 1024 * 1024, // 10MB
      },
      
      dynamodb: {
        tableName: 'safermark-users-prod',
        region: AWS_REGION,
      },
      
      lambda: {
        functionName: 'safermark-api-prod',
        runtime: 'nodejs20.x',
        timeout: 30,
        memorySize: 1024, // Higher memory for production
        environment: {
          NODE_ENV: 'production',
          SAFERMARK_ENV: 'prod',
        },
      },
      
      apiGateway: {
        domainName: 'api.safermark.com',
        throttling: {
          rateLimit: 5000,
          burstLimit: 10000,
        },
      },
      
      secretsManager: {
        secretName: 'safermark/prod/secrets',
        region: AWS_REGION,
      },
      
      cloudfront: {
        // Will be populated after initial deployment
      },
    },
    
    deployment: {
      enableCloudFront: true,
      enableCustomDomain: true,
      enableSsl: true,
      enableWaf: true,
      enableCloudWatch: true,
    },
    
    monitoring: {
      enableErrorAlerts: true,
      enablePerformanceAlerts: true,
      logRetentionDays: 90,
      enableXRayTracing: true,
    },
  },
};

/**
 * Get infrastructure configuration for a specific environment
 */
export function getInfrastructureConfig(environment: Environment): DeploymentConfig {
  return { ...INFRASTRUCTURE_CONFIGS[environment] };
}

/**
 * Get AWS configuration for a specific environment
 */
export function getAWSConfig(environment: Environment): AWSConfig {
  return getInfrastructureConfig(environment).aws;
}

/**
 * Get domain configuration for a specific environment
 */
export function getDomainConfig(environment: Environment): DomainConfig {
  return getInfrastructureConfig(environment).domains;
}

/**
 * Get Pulumi stack name for environment
 */
export function getStackName(environment: Environment): string {
  return INFRASTRUCTURE_CONFIGS[environment].stackName;
}

/**
 * Get resource name with environment prefix
 */
export function getResourceName(baseName: string, environment: Environment): string {
  return `${baseName}-${environment}`;
}

/**
 * Get S3 bucket name for environment
 */
export function getS3BucketName(environment: Environment): string {
  return getAWSConfig(environment).s3.bucketName;
}

/**
 * Get DynamoDB table name for environment
 */
export function getDynamoTableName(environment: Environment): string {
  return getAWSConfig(environment).dynamodb.tableName;
}

/**
 * Get Lambda function name for environment
 */
export function getLambdaFunctionName(environment: Environment): string {
  return getAWSConfig(environment).lambda.functionName;
}

/**
 * Get Secrets Manager secret name for environment
 */
export function getSecretName(environment: Environment): string {
  return getAWSConfig(environment).secretsManager.secretName;
}

/**
 * Build configuration for deployment scripts
 */
export interface BuildConfig {
  outputDir: string;
  sourceDir: string;
  excludePatterns: string[];
  includeSourceMaps: boolean;
  minify: boolean;
  target: 'node' | 'browser' | 'webworker';
}

export const BUILD_CONFIGS: Record<string, BuildConfig> = {
  api: {
    outputDir: 'packages/api/dist',
    sourceDir: 'packages/api/src',
    excludePatterns: ['**/*.test.ts', '**/*.spec.ts', '**/node_modules/**'],
    includeSourceMaps: false,
    minify: true,
    target: 'node',
  },
  
  web: {
    outputDir: 'packages/web/dist',
    sourceDir: 'packages/web/src',
    excludePatterns: ['**/*.test.ts', '**/*.spec.ts'],
    includeSourceMaps: false,
    minify: true,
    target: 'browser',
  },
  
  extension: {
    outputDir: 'packages/extension/dist',
    sourceDir: 'packages/extension/src',
    excludePatterns: ['**/*.test.ts', '**/*.spec.ts'],
    includeSourceMaps: false,
    minify: true,
    target: 'webworker',
  },
};

// Export constants
export { INFRASTRUCTURE_CONFIGS, AWS_ACCOUNT_ID, AWS_REGION };